import { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

type AuthContextType = {
  session: Session | null;
  user: User | null;
  loading: boolean;
};

const AuthContext = createContext<AuthContextType>({ session: null, user: null, loading: true });

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    console.log('Setting up auth subscription');
    
    let mounted = true;

    // Initialize session
    const initSession = async () => {
      try {
        // Use getSession instead of relying on localStorage
        const { data: { session }, error } = await supabase.auth.getSession();
        console.log('Initial session check:', { session, error });
        
        if (error) {
          console.error('Error getting session:', error);
          toast({
            title: "Authentication Error",
            description: "There was a problem with your session. Please try logging in again.",
            variant: "destructive",
          });
          // Clear any existing session
          await supabase.auth.signOut();
        } else if (mounted) {
          setSession(session);
          setUser(session?.user ?? null);
        }
      } catch (err) {
        console.error('Unexpected error during session initialization:', err);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initSession();

    // Set up auth state change listener with improved error handling
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, { user: session?.user?.id });
      
      if (!mounted) return;

      try {
        if (event === 'TOKEN_REFRESHED') {
          console.log('Token refreshed successfully');
        } else if (event === 'SIGNED_OUT') {
          console.log('User signed out, clearing session');
          setSession(null);
          setUser(null);
        } else if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
          console.log('User signed in or updated:', session?.user?.id);
          setSession(session);
          setUser(session?.user ?? null);
        }
      } catch (err) {
        console.error('Error handling auth state change:', err);
        toast({
          title: "Error",
          description: "There was a problem updating your session.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    });

    // Cleanup subscription and mounted flag
    return () => {
      console.log('Cleaning up auth subscription');
      mounted = false;
      subscription.unsubscribe();
    };
  }, [toast]);

  return (
    <AuthContext.Provider value={{ session, user, loading }}>
      {children}
    </AuthContext.Provider>
  );
};